import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { useAppStore } from "@hooks/useAppStore";
import { useAppTemplate } from "@hooks/useAppTemplate";
import useMixedItemDist from "@hooks/useMixedItemDist";
import { getCalendarDaysUntil } from "@memorang/helpers/date";
import { compareTagSortOrder, isSkillTag } from "@memorang/helpers/tag";
import type {
	MixedItemDist,
	MixedItemTag,
	RecommendationContext,
} from "@memorang/types";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
	type HomeSummaryTip,
	LANGUAGE_LEARNING_HOME_SUMMARY_USE_CASE,
	LANGUAGE_LEARNING_TIP_LEVEL_GUIDANCE,
	buildHomeSummaryTipData,
	buildLanguageLearningTipDataKey,
	buildTipSurfaceState,
	homeSummaryTipSchema,
	withTipAppFraming,
} from "../../tips/language-learning";
import { tipQueryOptions } from "../../tips/tipQueryOptions";
import { useParsedRecommendationContext } from "./useParsedRecommendationContext";

export type BuildHomeSummaryTipStateArgs = {
	mixedItemDist?: MixedItemDist;
	skillTags: MixedItemTag[];
	isLanguageLearningApp: boolean;
	recommendationContext?: RecommendationContext;
	appId?: string | number;
	template?: string;
	examId?: string | number;
	examDate?: string;
	daysUntilExam?: number;
	language: string;
	isDistLoading?: boolean;
};

export type HomeSummaryTipRequest = ReturnType<
	typeof buildHomeSummaryTipData
> & {
	app: string | number | undefined;
	template: string | undefined;
};

export type HomeSummaryTipState =
	| { enabled: false; isPending: boolean }
	| {
			enabled: true;
			data: HomeSummaryTipRequest;
			dataKey: string;
	  };

export const buildHomeSummaryTipState = ({
	mixedItemDist,
	skillTags,
	isLanguageLearningApp,
	recommendationContext,
	appId,
	template,
	examId,
	examDate,
	daysUntilExam,
	language,
	isDistLoading,
}: BuildHomeSummaryTipStateArgs): HomeSummaryTipState => {
	const surface = buildTipSurfaceState({
		isLanguageLearningApp,
		mixedItemDist,
		skillTags,
		recommendationContext,
		appId,
		template,
		examId,
		language,
	});
	if (!surface.enabled) {
		return {
			enabled: false,
			isPending: Boolean(isLanguageLearningApp && isDistLoading),
		};
	}

	const examFraming =
		examDate && daysUntilExam !== undefined && daysUntilExam >= 0
			? { examDate, daysUntilExam }
			: undefined;

	return {
		enabled: true,
		data: withTipAppFraming(
			appId,
			template,
			buildHomeSummaryTipData(
				surface.dist,
				surface.proficiencySummaries,
				examFraming,
			),
		),
		dataKey: buildLanguageLearningTipDataKey({
			...surface.cacheContext,
			daysUntilExam: examFraming?.daysUntilExam,
		}),
	};
};

export const toHomeSummaryTip = (
	state: HomeSummaryTipState,
	data: HomeSummaryTip | undefined,
	isLoading: boolean,
) =>
	state.enabled
		? {
				text: data?.text,
				isLoading,
			}
		: state.isPending
			? { text: undefined, isLoading: true }
			: undefined;

export const useHomeSummaryTip = ():
	| { text?: string; isLoading: boolean }
	| undefined => {
	// Same distribution as the home-header level tiles — the summary must
	// describe the levels the learner sees next to it.
	const { data: mixedItemDist, isLoading: isDistLoading } = useMixedItemDist();
	const recommendationContext = useParsedRecommendationContext();
	const { userPublicMetadataId } = useAuthContext();
	const {
		currentExamContext: { examDate, examId },
	} = useExamContext();
	const appId = useAppStore((state) => state.app.id);
	const { template, isLanguageLearningApp } = useAppTemplate();
	const { i18n } = useTranslation();

	const skillTags = useMemo(
		() =>
			(mixedItemDist?.tags ?? []).filter(isSkillTag).sort(compareTagSortOrder),
		[mixedItemDist],
	);
	const daysUntilExam = getCalendarDaysUntil(examDate);
	const state = useMemo(
		() =>
			buildHomeSummaryTipState({
				mixedItemDist,
				skillTags,
				isLanguageLearningApp,
				recommendationContext,
				appId,
				template,
				examId,
				examDate,
				daysUntilExam,
				language: i18n.language,
				isDistLoading,
			}),
		[
			mixedItemDist,
			skillTags,
			isLanguageLearningApp,
			recommendationContext,
			appId,
			template,
			examId,
			examDate,
			daysUntilExam,
			i18n.language,
			isDistLoading,
		],
	);

	const tipQuery = useQuery(
		tipQueryOptions({
			useCase: LANGUAGE_LEARNING_HOME_SUMMARY_USE_CASE,
			data: state.enabled ? state.data : undefined,
			dataKey: state.enabled ? state.dataKey : undefined,
			schema: homeSummaryTipSchema,
			userId: userPublicMetadataId,
			outputLanguage: i18n.language,
			additionalInstructions: LANGUAGE_LEARNING_TIP_LEVEL_GUIDANCE,
			enabled: state.enabled,
		}),
	);

	return toHomeSummaryTip(state, tipQuery.data, tipQuery.isLoading);
};
